import React, { useEffect, useRef, useState, useCallback } from "react";
import axios from "axios";
import { Palette, Eraser, Save, X, Brush, Sparkles, Timer, Plus, Minus } from "lucide-react";
import { drawCar } from "@/components/game/characters";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const TEMPLATES = [
  { id: "cruiser", label: "Pickup" },
  { id: "knight", label: "Knight" },
  { id: "tank", label: "Tank" },
  { id: "toon", label: "Toon" },
  { id: "hotrod", label: "Hot Rod" },
];

const PALETTE = [
  "#FF3B30", "#FFCC00", "#34C759", "#00F0FF", "#7B2CBF",
  "#FF1493", "#FFFFFF", "#000000", "#9c98a4", "#FF6B00",
  "#1E5BC8", "#3a3340",
];

const GRID_W = 240;
const GRID_H = 120;
const CELL_PX = 2; // canvas pixel size per cell — 10× denser than before so
                   // a single-cell stamp is genuinely small (was 18px @ 24×12)
const DRAW_CANVAS_W = GRID_W * CELL_PX;
const DRAW_CANVAS_H = GRID_H * CELL_PX;

const TIMER_SECONDS = 30;

/**
 * Pre-race sidekick designer.
 *
 * The player has 30 seconds to:
 *   1. Pick a base template (cruiser/knight/tank/toon/hotrod)
 *   2. Pick body + accent paint colors
 *   3. Draw pixel-art decals on a 240×120 grid laid over the car silhouette
 *   4. Name it (max 12 chars)
 *
 * On timeout OR when they hit "Save & Race", the design is POST'd to
 * /api/sidekick/design and we navigate to the race. If they timeout
 * without saving, whatever was on the canvas at T=0 is auto-saved.
 */
export default function SidekickDesigner({ onComplete, onCancel, noTimer = false }) {
  const [template, setTemplate] = useState("cruiser");
  const [body_color, setBody] = useState("#34C759");
  const [accent_color, setAccent] = useState("#FFCC00");
  const [name, setName] = useState("BUDDY");
  const [decals, setDecals] = useState([]); // array of {x, y, color}
  const [activeColor, setActiveColor] = useState("#FF3B30");
  const [tool, setTool] = useState("brush"); // brush | eraser
  // Brush radius in grid cells. 0 = single cell (default — fine work).
  // With the 240×120 grid, a single cell really is one tiny pixel, so the
  // size range goes higher than the old 24×12 grid (which only allowed 0-3).
  const [brushSize, setBrushSize] = useState(0);
  const [secondsLeft, setSecondsLeft] = useState(TIMER_SECONDS);
  const [saving, setSaving] = useState(false);

  const previewRef = useRef(null);
  const drawRef = useRef(null);
  const isDrawing = useRef(false);
  const designRef = useRef(null);
  // Always read the LATEST design from the ref inside the timer + save path
  // so handleSave doesn't capture a stale closure when called from the timer.
  designRef.current = { template, body_color, accent_color, name, decals };

  // ---- Hydrate from existing design when opened from Garage ---------------
  // The user expects to keep editing the buddy they already designed —
  // not start from scratch every time. This pre-load is gated on noTimer
  // (i.e. only Garage flow) so the race-launch path keeps its current
  // "fresh canvas every time" behavior.
  useEffect(() => {
    if (!noTimer) return;
    let cancelled = false;
    axios.get(`${BACKEND_URL}/api/sidekick/me`, { withCredentials: true })
      .then((r) => {
        if (cancelled) return;
        const d = r.data?.design;
        if (!d) return;
        if (d.template) setTemplate(d.template);
        if (d.body_color) setBody(d.body_color);
        if (d.accent_color) setAccent(d.accent_color);
        if (d.name) setName(d.name);
        if (Array.isArray(d.decals)) setDecals(d.decals);
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [noTimer]);

  // ---- 30-second countdown ------------------------------------------------
  // Skipped entirely when called from Garage (noTimer=true) — the user
  // explicitly wanted no time pressure when designing the buddy in the
  // garage, only race-launch sessions are time-boxed.
  useEffect(() => {
    if (noTimer) return undefined;
    if (secondsLeft <= 0) {
      void handleSave({ auto: true });
      return undefined;
    }
    const t = setTimeout(() => setSecondsLeft((s) => s - 1), 1000);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [secondsLeft, noTimer]);

  // ---- Preview canvas (renders the car + decals live) ---------------------
  useEffect(() => {
    const cvs = previewRef.current;
    if (!cvs) return;
    const ctx = cvs.getContext("2d");
    ctx.imageSmoothingEnabled = false;
    let raf;
    const render = () => {
      ctx.fillStyle = "#0a0a0a";
      ctx.fillRect(0, 0, cvs.width, cvs.height);
      // Pixel grid backdrop — every 10th cell only, otherwise the grid
      // becomes visual noise on the 240×120 canvas. The faint sub-grid
      // gives players a sense of scale without overwhelming the view.
      ctx.strokeStyle = "rgba(255,255,255,0.04)";
      ctx.lineWidth = 1;
      for (let i = 0; i <= GRID_W; i += 10) {
        ctx.beginPath();
        ctx.moveTo(i * CELL_PX, 0);
        ctx.lineTo(i * CELL_PX, DRAW_CANVAS_H);
        ctx.stroke();
      }
      for (let i = 0; i <= GRID_H; i += 10) {
        ctx.beginPath();
        ctx.moveTo(0, i * CELL_PX);
        ctx.lineTo(DRAW_CANVAS_W, i * CELL_PX);
        ctx.stroke();
      }
      // Render the car centered horizontally inside the grid
      ctx.save();
      drawCar(
        ctx,
        DRAW_CANVAS_W / 2,
        DRAW_CANVAS_H / 2,
        0,
        body_color,
        accent_color,
        1.6,
        template,
      );
      ctx.restore();
      // Render decals on top
      for (const c of decals) {
        ctx.fillStyle = c.color;
        ctx.fillRect(c.x * CELL_PX, c.y * CELL_PX, CELL_PX, CELL_PX);
      }
      raf = requestAnimationFrame(render);
    };
    render();
    return () => cancelAnimationFrame(raf);
  }, [template, body_color, accent_color, decals]);

  // ---- Drawing logic on the overlay canvas --------------------------------
  const cellAtEvent = (e) => {
    const cvs = drawRef.current;
    if (!cvs) return null;
    const rect = cvs.getBoundingClientRect();
    // Touch + mouse compatible
    const cx = e.touches?.[0]?.clientX ?? e.clientX;
    const cy = e.touches?.[0]?.clientY ?? e.clientY;
    const x = Math.floor(((cx - rect.left) / rect.width) * GRID_W);
    const y = Math.floor(((cy - rect.top) / rect.height) * GRID_H);
    if (x < 0 || x >= GRID_W || y < 0 || y >= GRID_H) return null;
    return { x, y };
  };

  const paintCell = useCallback((cell) => {
    if (!cell) return;
    setDecals((prev) => {
      // Build the set of cells under the brush stamp (square radius `brushSize`).
      const stamp = [];
      for (let dx = -brushSize; dx <= brushSize; dx++) {
        for (let dy = -brushSize; dy <= brushSize; dy++) {
          const x = cell.x + dx;
          const y = cell.y + dy;
          if (x < 0 || x >= GRID_W || y < 0 || y >= GRID_H) continue;
          stamp.push({ x, y });
        }
      }
      // Remove any existing decals at the stamp coords (so we don't pile
      // duplicate cells and waste the per-design cap).
      const stampSet = new Set(stamp.map((c) => `${c.x},${c.y}`));
      const filtered = prev.filter((c) => !stampSet.has(`${c.x},${c.y}`));
      if (tool === "eraser") return filtered;
      return [
        ...filtered,
        ...stamp.map((c) => ({ x: c.x, y: c.y, color: activeColor })),
      ];
    });
  }, [tool, activeColor, brushSize]);

  const handlePointerDown = (e) => {
    e.preventDefault();
    isDrawing.current = true;
    paintCell(cellAtEvent(e));
  };
  const handlePointerMove = (e) => {
    if (!isDrawing.current) return;
    e.preventDefault();
    paintCell(cellAtEvent(e));
  };
  const handlePointerUp = () => { isDrawing.current = false; };

  // ---- Save + complete ----------------------------------------------------
  const handleSave = async ({ auto = false } = {}) => {
    if (saving) return;
    setSaving(true);
    const d = designRef.current;
    try {
      const res = await axios.post(
        `${BACKEND_URL}/api/sidekick/design`,
        d,
        { withCredentials: true },
      );
      onComplete?.(res.data.design, auto);
    } catch (e) {
      // If the save fails we still complete (design is local only this race)
      onComplete?.(d, auto);
    }
  };

  const remainingPct = (secondsLeft / TIMER_SECONDS) * 100;

  return (
    <div
      className="fixed inset-0 z-[110] bg-black/95 backdrop-blur-sm overflow-y-auto"
      data-testid="sidekick-designer"
    >
      <div className="absolute inset-0 grid-bg opacity-25 pointer-events-none" />
      <div className="relative max-w-5xl mx-auto px-4 sm:px-6 py-6">
        {/* Header */}
        <div className="flex items-start justify-between mb-4">
          <div>
            <div className="label-kicker text-[#FFCC00] flex items-center gap-2">
              <Sparkles className="w-3.5 h-3.5" /> Sidekick Designer
            </div>
            <div className="font-heading text-3xl mt-1">Design your wingman.</div>
            <div className="text-xs text-white/50 mt-1">
              {noTimer
                ? "Pick a chassis, paint it, draw on it. Save whenever — no clock."
                : "30 seconds. Pick a chassis, paint it, draw on it. Hit Save when you're ready or it auto-saves at zero."}
            </div>
          </div>
          <button
            onClick={onCancel}
            className="text-white/40 hover:text-white"
            data-testid="sidekick-cancel"
            title="Skip designer"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Countdown timer — race-launch only. Hidden when called from
            Garage (noTimer=true) per user request: "no time limit to
            customize it". */}
        {!noTimer && (
          <div className="surface-glass border border-[#FFCC00]/40 px-4 py-3 mb-5 flex items-center gap-4">
            <Timer className={`w-5 h-5 ${secondsLeft <= 5 ? "text-[#FF3B30] anim-pulse-red" : "text-[#FFCC00]"}`} />
            <div className="flex-1">
              <div className="flex justify-between mb-1">
                <span className="text-[10px] tracking-[0.3em] uppercase text-white/60">
                  Time remaining
                </span>
                <span className={`font-mono text-sm tabular-nums ${secondsLeft <= 5 ? "text-[#FF3B30]" : "text-[#FFCC00]"}`} data-testid="sidekick-timer">
                  {String(secondsLeft).padStart(2, "0")}s
                </span>
              </div>
              <div className="h-1.5 bg-white/5 border border-white/10 overflow-hidden">
                <div
                  className={`h-full transition-all duration-1000 ${secondsLeft <= 5 ? "bg-[#FF3B30]" : "bg-[#FFCC00]"}`}
                  style={{ width: `${remainingPct}%` }}
                />
              </div>
            </div>
          </div>
        )}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
          {/* Left column — controls */}
          <div className="space-y-4">
            {/* Template picker */}
            <Section label="Chassis">
              <div className="grid grid-cols-3 gap-2">
                {TEMPLATES.map((t) => (
                  <button
                    key={t.id}
                    onClick={() => setTemplate(t.id)}
                    data-testid={`sidekick-template-${t.id}`}
                    className={`p-2 border text-xs uppercase tracking-wider transition-colors ${
                      template === t.id
                        ? "border-[#FFCC00] bg-[#FFCC00]/10 text-[#FFCC00]"
                        : "border-white/10 hover:border-white/30"
                    }`}
                  >
                    {t.label}
                  </button>
                ))}
              </div>
            </Section>

            {/* Body color */}
            <Section label="Body Paint" icon={Palette}>
              <ColorPicker value={body_color} onChange={setBody} testidPrefix="sidekick-body" />
            </Section>

            {/* Accent color */}
            <Section label="Accent" icon={Palette}>
              <ColorPicker value={accent_color} onChange={setAccent} testidPrefix="sidekick-accent" />
            </Section>

            {/* Name */}
            <Section label="Name (max 12)">
              <input
                value={name}
                onChange={(e) => setName(e.target.value.slice(0, 12).toUpperCase())}
                maxLength={12}
                data-testid="sidekick-name-input"
                className="w-full bg-black/40 border border-white/10 px-3 py-2 font-heading text-base tracking-widest uppercase focus:border-[#FFCC00] outline-none"
                placeholder="BUDDY"
              />
            </Section>
          </div>

          {/* Center column — canvas */}
          <div className="lg:col-span-2">
            <div className="surface-solid p-4">
              <div className="flex items-center gap-2 mb-3">
                <span className="label-kicker">Draw on the grid</span>
                <span className="h-px flex-1 bg-white/10" />
                {/* Brush size stepper — 1 (single cell), 3, 5, 7 */}
                <div className="flex items-center border border-white/10 overflow-hidden" data-testid="sidekick-brush-size">
                  <button
                    onClick={() => setBrushSize((s) => Math.max(0, s - 1))}
                    disabled={brushSize === 0}
                    data-testid="sidekick-brush-smaller"
                    className={`px-2 py-1.5 ${brushSize === 0 ? "text-white/20 cursor-not-allowed" : "text-white/70 hover:text-[#FFCC00]"}`}
                    title="Smaller brush"
                  >
                    <Minus className="w-3 h-3" />
                  </button>
                  <span
                    className="px-2 font-mono text-[10px] tracking-wider min-w-[28px] text-center"
                    data-testid="sidekick-brush-readout"
                  >
                    {brushSize * 2 + 1}px
                  </span>
                  <button
                    onClick={() => setBrushSize((s) => Math.min(12, s + 1))}
                    disabled={brushSize === 12}
                    data-testid="sidekick-brush-bigger"
                    className={`px-2 py-1.5 ${brushSize === 12 ? "text-white/20 cursor-not-allowed" : "text-white/70 hover:text-[#FFCC00]"}`}
                    title="Bigger brush"
                  >
                    <Plus className="w-3 h-3" />
                  </button>
                </div>
                <button
                  onClick={() => setTool("brush")}
                  data-testid="sidekick-tool-brush"
                  className={`p-2 border ${
                    tool === "brush"
                      ? "border-[#FFCC00] text-[#FFCC00]"
                      : "border-white/10 text-white/60"
                  }`}
                  title="Brush"
                >
                  <Brush className="w-3.5 h-3.5" />
                </button>
                <button
                  onClick={() => setTool("eraser")}
                  data-testid="sidekick-tool-eraser"
                  className={`p-2 border ${
                    tool === "eraser"
                      ? "border-[#FF3B30] text-[#FF3B30]"
                      : "border-white/10 text-white/60"
                  }`}
                  title="Eraser"
                >
                  <Eraser className="w-3.5 h-3.5" />
                </button>
                <button
                  onClick={() => setDecals([])}
                  data-testid="sidekick-clear"
                  className="px-2 py-1 text-[10px] uppercase tracking-widest border border-white/10 hover:border-[#FF3B30] hover:text-[#FF3B30]"
                >
                  Clear
                </button>
              </div>

              {/* Live preview canvas with overlay drawing canvas */}
              <div className="relative w-full" style={{ aspectRatio: `${GRID_W}/${GRID_H}` }}>
                <canvas
                  ref={previewRef}
                  width={DRAW_CANVAS_W}
                  height={DRAW_CANVAS_H}
                  className="absolute inset-0 w-full h-full"
                  style={{ imageRendering: "pixelated" }}
                  data-testid="sidekick-preview-canvas"
                />
                {/* Transparent draw layer captures pointer events */}
                <canvas
                  ref={drawRef}
                  width={DRAW_CANVAS_W}
                  height={DRAW_CANVAS_H}
                  className="absolute inset-0 w-full h-full cursor-crosshair touch-none"
                  onMouseDown={handlePointerDown}
                  onMouseMove={handlePointerMove}
                  onMouseUp={handlePointerUp}
                  onMouseLeave={handlePointerUp}
                  onTouchStart={handlePointerDown}
                  onTouchMove={handlePointerMove}
                  onTouchEnd={handlePointerUp}
                  data-testid="sidekick-draw-canvas"
                />
              </div>

              {/* Active color palette */}
              <div className="mt-4">
                <div className="label-kicker mb-2">Brush color</div>
                <ColorPicker
                  value={activeColor}
                  onChange={setActiveColor}
                  testidPrefix="sidekick-brush"
                />
              </div>
            </div>

            <button
              onClick={() => handleSave({ auto: false })}
              disabled={saving}
              data-testid="sidekick-save-btn"
              className={`mt-4 w-full py-3 font-heading text-sm uppercase tracking-[0.3em] flex items-center justify-center gap-2 transition-all ${
                saving
                  ? "bg-white/5 text-white/30 cursor-not-allowed"
                  : "bg-[#FFCC00] text-black hover:bg-[#FFD633]"
              }`}
            >
              <Save className="w-4 h-4" /> {saving ? "Saving…" : "Save & Race"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function Section({ label, icon: Icon, children }) {
  return (
    <div className="surface-solid p-3">
      <div className="flex items-center gap-2 mb-2">
        {Icon && <Icon className="w-3 h-3 text-[#FFCC00]" />}
        <span className="label-kicker">{label}</span>
      </div>
      {children}
    </div>
  );
}

function ColorPicker({ value, onChange, testidPrefix }) {
  return (
    <div className="grid grid-cols-6 gap-1.5">
      {PALETTE.map((c) => (
        <button
          key={c}
          onClick={() => onChange(c)}
          data-testid={`${testidPrefix}-${c.replace("#", "")}`}
          className={`h-8 border-2 transition-all ${
            value === c ? "border-[#FFCC00] scale-110" : "border-white/10 hover:border-white/30"
          }`}
          style={{ background: c }}
          aria-label={`Color ${c}`}
        />
      ))}
    </div>
  );
}
